import React from 'react';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import cx from 'classnames';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import app from 'new-ui/app';
import { useTranslation } from 'utils/modifiedTranslation';
import { REMATCH_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import configs from 'configs';
import { createUserFinance } from 'pages/FinanceSupport/createUserFinance';
import { TAB_IDS } from '../constants';
import { SUPPORTED_TAB_IDS } from './constants';
import styles from './menuItems.module.css';

const GA = {
  category: 'Finance',
  events: {
    clickFinancialTab: 'Clicking on the “Financial support” tab',
    clickTreatmentTab: 'Clicking on the “Treatment options” tab',
    clickSymptomCareTab: 'Clicking on the “Symptom Care” tab',
    clickArticlesTab: 'Clicking on the “Articles” tab',
  },
};

const GA_CHAT = {
  category: 'Chat',
  events: {
    clickSpeakWithTinaTab: 'Clicking on the “Speak with Tina” tab',
  },
};

export const MenuItems = ({
  tabIds, onMenuItemClick, isAuthenticated, activeItem, userCondition, classes,
}) => {
  const { t } = useTranslation();

  return (
    <ul className={cx(styles.root, classes.root)}>
      {tabIds.includes(TAB_IDS.FEED) && (
        <li
          className={cx(classes.item, { [classes.activeItem]: TAB_IDS.FEED === activeItem })}
          onClick={() => onMenuItemClick((dispatch) => {
            dispatch(push(ROUTE_PATH[ROUTE_NAME.FEED]));
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickFinancialTab, { name: 'Feed tab clicked' });
          })}
        >
          <div className={classes.itemTitle}>
            {t('general.feed')}
          </div>
        </li>
      )}
      {tabIds.includes(TAB_IDS.TREATMENT_OPTIONS) && (
        <li
          className={cx(classes.item, { [classes.activeItem]: TAB_IDS.TREATMENT_OPTIONS === activeItem })}
          onClick={() => onMenuItemClick((dispatch) => {
            if (!isAuthenticated) {
              createUserFinance({
                redirectTo: `${ROUTE_PATH[ROUTE_NAME.RESULTS]}?${REMATCH_URL_SEARCH_PARAM}=1`,
                subtitle: t('support_programs.create_user_subtitle_header'),
              });
              return;
            }
            dispatch(push(ROUTE_PATH[ROUTE_NAME.RESULTS]));
          })}
        >
          <div
            className={classes.itemTitle}
            onClick={() => {
              app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickTreatmentTab, { name: 'Treatment options tab' });
            }}
          >
            {t('general.treatment_options')}
          </div>
        </li>
      )}
      {tabIds.includes(TAB_IDS.SUPPORT_PROGRAMS) && (
        <li
          className={cx(classes.item, { [classes.activeItem]: TAB_IDS.SUPPORT_PROGRAMS === activeItem })}
          onClick={() => onMenuItemClick((dispatch) => {
            dispatch(push(ROUTE_PATH[ROUTE_NAME.FINANCE]));
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickFinancialTab, { name: 'Financial support tab clicked' });
          })}
        >
          <div className={classes.itemTitle}>
            {t('general.support_programs')}
          </div>
        </li>
      )}

      {tabIds.includes(TAB_IDS.SYMPTOM_CARE) && (
        <li
          className={cx(classes.item)}
          onClick={() => onMenuItemClick(() => {
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickSymptomCareTab, { name: 'Symptom Care tab clicked' });
            const url = new URL(`${configs.sideEffectsUrl}/search`);
            if (userCondition) {
              url.searchParams.append('cancer_type', userCondition);
              url.searchParams.append('user', 'registered');
            }
            window.open(url.toString(), '_blank');
          })}
        >
          <div className={classes.itemTitle}>{t('general.symptom_care')}</div>
        </li>
      )}

      {tabIds.includes(TAB_IDS.ARTICLES) && (
        <li
          className={cx(classes.item, { [classes.activeItem]: TAB_IDS.ARTICLES === activeItem })}
          onClick={() => onMenuItemClick((dispatch) => {
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickArticlesTab, { name: 'Articles tab clicked' });
            dispatch(push(ROUTE_PATH[ROUTE_NAME.ARTICLES]));
          })}
        >
          <div className={classes.itemTitle}>{t('general.articles')}</div>
        </li>
      )}

      {tabIds.includes(TAB_IDS.SPEAK_WITH_TINA) && (
        <li
          className={cx(classes.item, classes.tinaItem, { [classes.activeItem]: TAB_IDS.SPEAK_WITH_TINA === activeItem })}
          onClick={() => onMenuItemClick((dispatch) => {
            app.sendGoogleAnalyticsEvent(GA_CHAT.category, GA_CHAT.events.clickSpeakWithTinaTab, { name: 'speack with tina tab clicked' });
            dispatch(push(ROUTE_PATH[ROUTE_NAME.CHAT]));
          })}
        >
          <div className={classes.itemTitle}>
            {t('general.speak_with_tina')}
            <span className={classes.tinaEmoji}>😊</span>
          </div>
        </li>
      )}
    </ul>
  );
};

MenuItems.propTypes = {
  tabIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(SUPPORTED_TAB_IDS))).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  onMenuItemClick: PropTypes.func.isRequired,
  activeItem: PropTypes.string,
  // user can be not authenticated yet
  userCondition: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string,
    item: PropTypes.string.isRequired,
    activeItem: PropTypes.string.isRequired,
    itemTitle: PropTypes.string.isRequired,
    tinaItem: PropTypes.string.isRequired,
    tinaEmoji: PropTypes.string.isRequired,
  }).isRequired,
};
